import React, { useEffect, useRef, useState } from 'react';
import close from '../../assets/icons/close.svg';
import { getData, Postdataform } from '../../API/GlobalApi';
import StudentSearch from '../SearchStudent';
import useAutoFocus from '../../Utils/autoFocus';
import { numberToWords } from '../../Utils/convertNumtoWord';
import '../../assets/css/Modal.css';
import Select from 'react-select';
import { reactSelectStyles } from '../../Utils/selectBoxStyle';
import '../../assets/css/StudentShortDetails.css';
import Notifier from '../../Utils/notify';
import { formatDateDDMMYYYYHHMMSS_UTC } from '../../Utils/data&dateTime';
import { useFocusTrap } from '../../Utils/TabIndexingFocus';

// A refund is money leaving the college, so the POS capture modes (Card, UPI,
// Wallet, EMI) and gateway settlements are out — those get reversed through the
// gateway, not from this counter. Mirrors UNSUPPORTED_REFUND_METHODS in
// finance/views/refunds.py; the backend rejects them too.
const UNSUPPORTED_REFUND_MODES = [
    'Card', 'UPI', 'Wallet', 'EMI', 'Gateway', 'POS',
    'Cash in Bank', 'Cheque in Bank', 'Demand Draft in Bank', 'UPI in Bank',
];

export const RefundPayment = ({
    isRefundModalOpen,
    onClose,
    modeOfPayment,
}) => {
    const [amount, setAmount] = useState('');
    const [studentsData, setStudentsData] = useState([]);
    const [selectedRefundMode, setSelectedRefundMode] = useState(null);
    const [remarks, setRemarks] = useState('');
    const [showLedger, setShowLedger] = useState(false);
    const [resetSearchKey, setResetSearchKey] = useState(0);
    const [errors, setErrors] = useState({});
    const [loadingButton, setLoadingButton] = useState(null);

    // Cheque
    const [chequeNumber, setChequeNumber] = useState('');
    const [chequeBankId, setChequeBankId] = useState('');
    const [ifscCode, setIfscCode] = useState('');
    const [chequeDate, setChequeDate] = useState('');
    const [chequeImage, setChequeImage] = useState(null);

    // Demand Draft
    const [ddNumber, setDdNumber] = useState('');
    const [ddBankId, setDdBankId] = useState('');
    const [issuingBranchId, setIssuingBranchId] = useState('');
    const [ddIssuingDate, setDdIssuingDate] = useState('');
    const [ddpayeeName, setDdPayeeName] = useState('');
    const [payAbleAt, setPayAbleAt] = useState('');
    const [ddImage, setDDImage] = useState(null);

    // NEFT / RTGS / IMPS
    const [utrNumber, setUtrNumber] = useState('');
    const [bankAccount, setBankAccount] = useState(null);

    const [bankNames, setBankNames] = useState([]);
    const [bankAccountOptions, setBankAccountOptions] = useState([]);

    const nameInputRef = useAutoFocus(isRefundModalOpen);
    const trapRef = useFocusTrap(isRefundModalOpen);
    const refundModeRef = useRef(null);
    const amountRef = useRef(null);

    const bankOptions = bankNames?.map((bank) => ({ value: bank.id, label: bank.name })) || [];

    const refundModeOptions = (modeOfPayment || [])
        .filter((mode) => !UNSUPPORTED_REFUND_MODES.includes(mode))
        .map((mode) => ({ value: mode, label: mode }));

    const ledgerEntries = studentsData?.finance_data?.ledger_entries || [];
    const unifiedEntries = ledgerEntries
        .map((e) => ({
            ...e,
            entryType: e.payment_type === 'Debit' ? 'Debit' : 'Credit',
            sortDate: e.payment_type === 'Credit' && e.payment_date ? e.payment_date : e.created_at,
        }))
        .sort((a, b) => new Date(a.sortDate) - new Date(b.sortDate));

    let totalDebit = 0;
    let totalCredit = 0;
    unifiedEntries.forEach((item) => {
        if (item.entryType === 'Debit') totalDebit += item.amount;
        if (item.entryType === 'Credit') totalCredit += item.amount;
    });
    const finalBalance = totalCredit - totalDebit;

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [bankResponse, bankDetailsResponse] = await Promise.all([
                    getData('finance/institution-bank-accounts/'),
                    getData('staff/teacher-official-details/get-master-data/'),
                ]);

                if (Array.isArray(bankResponse)) {
                    const options = bankResponse.map((acc) => ({
                        value: acc.id,
                        label: `A/C: ${acc.account_number} - ${acc.account_holder_name} - ${acc.bank_name}`,
                        isDefault: acc.is_default,
                    }));
                    setBankAccountOptions(options);

                    const defaultAcc = options.find((acc) => acc.isDefault);
                    if (defaultAcc) setBankAccount(defaultAcc);
                }

                setBankNames(bankDetailsResponse?.master?.banks || []);
            } catch (error) {
                console.error('Error fetching refund master data:', error);
            }
        };

        fetchData();
    }, []);

    useEffect(() => {
        if (studentsData?.user_id) {
            setTimeout(() => {
                refundModeRef.current?.focus();
                refundModeRef.current?.openMenu();
            }, 100);
        }
    }, [studentsData]);

    const handleModalClose = () => {
        onClose();
        resetForm();
    };

    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === 'Escape' && isRefundModalOpen) handleModalClose();
        };
        window.addEventListener('keydown', handleEscKey);
        return () => window.removeEventListener('keydown', handleEscKey);
    }, [isRefundModalOpen]);

    const validateField = (name, value) => {
        switch (name) {
            case 'ifscCode':
                if (!value.trim()) return 'IFSC code is required.';
                if (!/^[A-Z]{4}0[A-Z0-9]{6}$/.test(value.toUpperCase())) {
                    return 'Invalid IFSC format (e.g., SBIN0001234).';
                }
                break;
            case 'chequeNumber':
                if (!value.trim()) return 'Cheque number is required.';
                if (!/^[0-9]{6}$/.test(value)) return 'Cheque number must be 6 digits.';
                break;
            case 'ddNumber':
                if (!value.trim()) return 'Demand Draft number is required.';
                if (!/^[0-9]{6,9}$/.test(value)) return 'DD number must be 6–9 digits.';
                break;
            case 'utrNumber':
                if (!value.trim()) return 'UTR number is required.';
                if (!/^[A-Z0-9/_-]{12,30}$/.test(value.toUpperCase())) {
                    return 'UTR must be 12–30 characters (alphanumeric, /, -, _).';
                }
                break;
            case 'chequeImage':
            case 'ddImage': {
                if (!value) return 'File is required.';
                const allowedTypes = ['image/jpeg', 'image/png', 'application/pdf'];
                if (!allowedTypes.includes(value.type)) return 'Only JPG, PNG, or PDF files are allowed.';
                if (value.size > 2 * 1024 * 1024) return 'File size must be less than 2MB.';
                break;
            }
            case 'issuingBranchId':
                if (!value.trim()) return 'Issuing Branch Name is required.';
                if (value.length < 3) return 'Branch Name must be at least 3 characters.';
                if (!/^[A-Za-z0-9\s,.-]+$/.test(value)) {
                    return 'Branch Name should contain only letters, numbers, spaces, commas, or dashes.';
                }
                break;
            case 'ddpayeeName':
                if (!value.trim()) return 'Payee Name is required.';
                if (!/^[A-Za-z\s]{3,}$/.test(value)) return 'Payee Name must be alphabets only (min 3 chars).';
                break;
            case 'payAbleAt':
                if (!value.trim()) return 'Payable At is required.';
                if (value.length < 3) return 'Payable At must be at least 3 characters.';
                if (!/^[A-Za-z\s]+$/.test(value)) return 'Payable At should contain only letters.';
                break;
            case 'chequeDate':
            case 'ddIssuingDate':
                if (!value) return 'Date is required.';
                break;
            // Mandatory: the approver reads this verbatim in the WhatsApp message.
            case 'remarks':
                if (!value.trim()) return 'Reason for refund is required.';
                if (value.trim().length < 3) return 'Reason must be at least 3 characters.';
                break;
            default:
                break;
        }
        return '';
    };

    const handleInputChange = (field, value) => {
        switch (field) {
            case 'chequeNumber':
                setChequeNumber(value.replace(/\D/g, ''));
                break;
            case 'chequeBankId':
                setChequeBankId(value);
                break;
            case 'ifscCode':
                setIfscCode(value.toUpperCase());
                break;
            case 'chequeDate':
                setChequeDate(value);
                break;
            case 'chequeImage':
                setChequeImage(value);
                break;
            case 'ddNumber':
                setDdNumber(value.replace(/\D/g, ''));
                break;
            case 'ddBankId':
                setDdBankId(value);
                break;
            case 'issuingBranchId':
                setIssuingBranchId(value);
                break;
            case 'ddIssuingDate':
                setDdIssuingDate(value);
                break;
            case 'ddpayeeName':
                setDdPayeeName(value.replace(/[^a-zA-Z\s]/g, ''));
                break;
            case 'payAbleAt':
                setPayAbleAt(value.replace(/[^a-zA-Z\s]/g, ''));
                break;
            case 'ddImage':
                setDDImage(value);
                break;
            case 'utrNumber':
                setUtrNumber(value.toUpperCase());
                break;
            case 'bankAccount':
                setBankAccount(value);
                break;
            case 'remarks':
                setRemarks(value);
                break;
            default:
                break;
        }

        setErrors((prev) => ({ ...prev, [field]: validateField(field, value) }));
    };

    const values = {
        chequeNumber, chequeBankId, ifscCode, chequeImage, chequeDate,
        ddNumber, ddBankId, ddIssuingDate, ddpayeeName, payAbleAt, issuingBranchId, ddImage,
        utrNumber, bankAccount,
    };

    const validateRefund = (mode) => {
        const rules = {
            Cheque: ['chequeNumber', 'chequeBankId', 'ifscCode', 'chequeImage', 'chequeDate'],
            'Demand Draft': [
                'ddNumber', 'ddBankId', 'ddIssuingDate', 'ddpayeeName',
                'payAbleAt', 'issuingBranchId', 'ddImage',
            ],
            NEFT: ['utrNumber', 'bankAccount'],
            RTGS: ['utrNumber', 'bankAccount'],
            IMPS: ['utrNumber', 'bankAccount'],
        };

        if (!mode) {
            Notifier.error('Please select a mode of refund');
            return false;
        }

        const remarksError = validateField('remarks', remarks);
        if (remarksError) {
            setErrors((prev) => ({ ...prev, remarks: remarksError }));
            Notifier.error(remarksError);
            return false;
        }

        const requiredFields = rules[mode];
        if (requiredFields) {
            for (const field of requiredFields) {
                if (!values[field]) {
                    Notifier.error(`Please fill all ${mode} details.`);
                    return false;
                }
            }
        }
        return true;
    };

    const resetForm = () => {
        setStudentsData([]);
        setSelectedRefundMode(null);
        setAmount('');
        setRemarks('');
        setErrors({});
        setResetSearchKey((prev) => prev + 1);
        setChequeNumber('');
        setChequeBankId('');
        setIfscCode('');
        setChequeDate('');
        setChequeImage(null);
        setDdNumber('');
        setDdBankId('');
        setIssuingBranchId('');
        setDdIssuingDate('');
        setDdPayeeName('');
        setPayAbleAt('');
        setDDImage(null);
        setUtrNumber('');
    };

    const handleRefund = async (actionType) => {
        try {
            setLoadingButton(actionType);
            const mode = selectedRefundMode?.value;

            if (!validateRefund(mode)) {
                setLoadingButton(null);
                return;
            }

            const formData = new FormData();
            formData.append('student_user_id', studentsData?.user_id);
            formData.append('refund_method', mode);
            formData.append('amount', parseFloat(amount));
            formData.append('remarks', remarks.trim());

            if (mode === 'Cheque') {
                formData.append('cheque_number', chequeNumber);
                formData.append('bank_id', chequeBankId);
                formData.append('ifsc_code', ifscCode);
                formData.append('cheque_image', chequeImage);
                formData.append('cheque_date', chequeDate);
            }
            if (mode === 'Demand Draft') {
                formData.append('dd_number', ddNumber);
                formData.append('bank_id', ddBankId);
                formData.append('issuing_branch', issuingBranchId);
                formData.append('date_of_issuing', ddIssuingDate);
                formData.append('payee_name', ddpayeeName);
                formData.append('payable_at', payAbleAt);
                formData.append('dd_image', ddImage);
            }
            if (['NEFT', 'RTGS', 'IMPS'].includes(mode)) {
                formData.append('utr_number', utrNumber);
                formData.append('institution_bank_id', bankAccount?.value);
            }

            const response = await Postdataform('finance/refunds/create/', formData);

            if (response?.error) {
                const errorMessage = `${response.error}${response.details ? `: ${response.details}` : ''}`;
                Notifier.error(errorMessage);
                return;
            }

            // The refund is only a request until an approver taps Confirm on
            // WhatsApp — say so rather than implying the money has moved.
            if (response?.approvers_notified) {
                Notifier.success(response?.message || 'Refund request sent for approval');
            } else {
                Notifier.warning(response?.message || 'Refund request saved, but no approver was notified.');
            }

            resetForm();

            if (actionType === 'close') {
                setTimeout(() => onClose(), 50);
            }
        } catch (error) {
            Notifier.error(
                error?.response?.data?.message || 'Failed to raise the refund. Please try again.'
            );
        } finally {
            setLoadingButton(null);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && amount && remarks.trim() && studentsData?.user_id) {
            e.preventDefault();
            handleRefund('new');
        }
    };

    if (!isRefundModalOpen) return null;

    const isDisabled = loadingButton || !studentsData?.user_id || !amount || !remarks.trim();
    const buttonStyle = {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        cursor: isDisabled ? 'not-allowed' : 'pointer',
        backgroundColor: isDisabled ? '#d3d3d3' : '#7F56DA',
    };

    return (
        <div className="modal-overlay">
            <div
                className="modal-content recevingPaymentBody"
                style={{
                    position: 'relative',
                    width: '70%',
                    display: 'flex',
                    flexDirection: 'column',
                    overflowY: 'auto',
                    height: '94vh',
                }}
                ref={trapRef}
            >
                <div
                    className="modal-header-content"
                    style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: '10px',
                    }}
                >
                    <h2 className="text-primary">Refund Payment</h2>
                    <button className="close-btn" onClick={handleModalClose}>
                        <img src={close} alt="close" />
                    </button>
                </div>

                <div>
                    <div className="mb-4">
                        <div className="row">
                            <div className="col-md-12">
                                <StudentSearch
                                    studentsData={studentsData}
                                    nameInputRef={nameInputRef}
                                    resetTrigger={resetSearchKey}
                                    setStudentsData={setStudentsData}
                                />
                            </div>
                        </div>
                    </div>

                    <div className="row" style={{ background: '#F9FAFC', padding: '10px' }}>
                        <div className="row" style={{ background: '#F9FAFC', padding: '10px' }}>
                            <div className="col-md-3">
                                <h4 className="fm-pr-hd">DETAILS</h4>
                            </div>
                        </div>

                        {/* Left side */}
                        <div className="col-md-4">
                            <div className="student-details" style={{ textAlign: 'left' }}>
                                {studentsData?.profile_name === 'Student' && (
                                    <div>
                                        <p className="sd-p">Reg No</p>
                                        <p className="n-p-t">{studentsData?.permanent_registration_number}</p>
                                    </div>
                                )}

                                <div>
                                    <p className="sd-p">Name</p>
                                    {studentsData?.profile_name === 'Student' ? (
                                        <p className="n-p-t">
                                            {[
                                                studentsData?.salutation,
                                                studentsData?.first_name,
                                                studentsData?.middle_name,
                                                studentsData?.last_name,
                                            ]
                                                .filter((item) => item && item.trim() !== '')
                                                .join(' ')}
                                        </p>
                                    ) : (
                                        <p className="n-p-t">{studentsData?.full_name}</p>
                                    )}
                                </div>

                                <div>
                                    <p className="sd-p">Contact No</p>
                                    <p className="n-p-t">{studentsData?.mobile}</p>
                                </div>
                            </div>
                        </div>

                        {/* Right side */}
                        <div className="col-md-4 d-flex flex-column">
                            <div>
                                <p className="sd-p">Profile type</p>
                                <p className="n-p-t">{studentsData?.profile_name}</p>
                            </div>

                            {studentsData?.profile_name === 'Student' && (
                                <div>
                                    <p className="sd-p">Course</p>
                                    <p className="n-p-t">{studentsData?.lag?.display}</p>
                                </div>
                            )}

                            {studentsData?.profile_name === 'Student' && (
                                <div>
                                    <p className="mt-3" style={{ color: '#7f56da', fontWeight: 'bold' }}>
                                        Balance
                                    </p>
                                    <p
                                        style={{
                                            color:
                                                Number(
                                                    studentsData?.finance_data?.financial_summary?.outstanding_balance
                                                ) < 0
                                                    ? 'red'
                                                    : 'green',
                                            fontWeight: '600',
                                        }}
                                    >
                                        {studentsData?.finance_data?.financial_summary?.outstanding_balance}
                                    </p>
                                </div>
                            )}
                        </div>

                        <div className="col-md-4 d-flex justify-content-center">
                            {studentsData?.profile_picture?.url && (
                                <img
                                    src={`${process.env.REACT_APP_API_BASE_URL}${studentsData?.profile_picture?.url}`}
                                    alt="Student"
                                    style={{
                                        width: '120px',
                                        height: '120px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        marginBottom: '15px',
                                    }}
                                />
                            )}
                        </div>
                    </div>

                    <div
                        className="row mt-3 d-flex justify-content-center m-auto"
                        style={{ maxWidth: '90%' }}
                    >
                        <div className="col-md-10">
                            <div
                                className="payment-form"
                                style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
                            >
                                <div className="row">
                                    <div className="col-md-4">
                                        <label className="form-labell">
                                            Mode of Refund<span className="astrisk">*</span>
                                        </label>
                                    </div>
                                    <div className="col-md-8">
                                        <Select
                                            id="refundMode"
                                            className="basic-single"
                                            classNamePrefix="select"
                                            options={refundModeOptions}
                                            ref={refundModeRef}
                                            value={selectedRefundMode}
                                            onChange={(value) => {
                                                setSelectedRefundMode(value);
                                                setTimeout(() => amountRef.current?.focus(), 50);
                                            }}
                                            menuPlacement="auto"
                                            menuPosition="fixed"
                                            isSearchable
                                            placeholder="Select Refund Mode"
                                            styles={{
                                                ...reactSelectStyles,
                                                menuPortal: (base) => ({ ...base, zIndex: 999999999 }),
                                            }}
                                            menuPortalTarget={null}
                                        />
                                    </div>
                                </div>

                                {selectedRefundMode?.value === 'Cheque' && (
                                    <>
                                        <div className="row">
                                            <div className="col-md-4">
                                                <label className="form-labell">
                                                    Cheque Number<span className="astrisk">*</span>
                                                </label>
                                            </div>
                                            <div className="col-md-8">
                                                <input
                                                    type="text"
                                                    className="form-control fm-modal"
                                                    value={chequeNumber}
                                                    placeholder="Enter cheque number"
                                                    onChange={(e) => handleInputChange('chequeNumber', e.target.value)}
                                                />
                                                {errors.chequeNumber && (
                                                    <p className="text-danger">{errors.chequeNumber}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4">
                                                <label className="form-labell">
                                                    Bank Name<span className="astrisk">*</span>
                                                </label>
                                            </div>
                                            <div className="col-md-8">
                                                <Select
                                                    options={bankOptions}
                                                    value={bankOptions.find((b) => b.value === chequeBankId) || null}
                                                    onChange={(bank) => handleInputChange('chequeBankId', bank?.value || '')}
                                                    placeholder="Select bank..."
                                                    isSearchable
                                                    styles={reactSelectStyles}
                                                />
                                                {errors.chequeBankId && (
                                                    <p className="text-danger">{errors.chequeBankId}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4">
                                                <label className="form-labell">
                                                    IFSC Code<span className="astrisk">*</span>
                                                </label>
                                            </div>
                                            <div className="col-md-8">
                                                <input
                                                    type="text"
                                                    className="form-control fm-modal"
                                                    value={ifscCode}
                                                    onChange={(e) => handleInputChange('ifscCode', e.target.value)}
                                                    placeholder="Enter IFSC code"
                                                />
                                                {errors.ifscCode && <p className="text-danger">{errors.ifscCode}</p>}
                                            </div>
                                        </div>

                                        <div className="row mt-3">
                                            <div className="col-md-4">
                                                <label className="form-labell">
                                                    Upload Cheque Image<span className="astrisk">*</span>
                                                </label>
                                            </div>
                                            <div className="col-md-8">
                                                <input
                                                    type="file"
                                                    accept="image/*"
                                                    className="form-control fm-modal"
                                                    onChange={(e) => handleInputChange('chequeImage', e.target.files[0])}
                                                />
                                                {errors.chequeImage && (
                                                    <p className="text-danger">{errors.chequeImage}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4">
                                                <label className="form-labell">
                                                    Cheque Date<span className="astrisk">*</span>
                                                </label>
                                            </div>
                                            <div className="col-md-8">
                                                <input
                                                    type="date"
                                                    className="form-control fm-modal"
                                                    value={chequeDate}
                                                    onChange={(e) => handleInputChange('chequeDate', e.target.value)}
                                                    min={new Date().toISOString().split('T')[0]}
                                                />
                                                {errors.chequeDate && (
                                                    <p className="text-danger">{errors.chequeDate}</p>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}

                                {selectedRefundMode?.value === 'Demand Draft' && (
                                    <>
                                        <div className="row">
                                            <div className="col-md-4">
                                                <label className="form-labell">
                                                    Demand Draft Number<span className="astrisk">*</span>
                                                </label>
                                            </div>
                                            <div className="col-md-8">
                                                <input
                                                    type="text"
                                                    className="form-control fm-modal"
                                                    value={ddNumber}
                                                    onChange={(e) => handleInputChange('ddNumber', e.target.value)}
                                                    placeholder="Enter DD number"
                                                />
                                                {errors.ddNumber && <p className="text-danger">{errors.ddNumber}</p>}
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4">
                                                <label className="form-labell">
                                                    Bank Name<span className="astrisk">*</span>
                                                </label>
                                            </div>
                                            <div className="col-md-8">
                                                <Select
                                                    options={bankOptions}
                                                    value={bankOptions.find((b) => b.value === ddBankId) || null}
                                                    onChange={(bank) => handleInputChange('ddBankId', bank?.value || '')}
                                                    placeholder="Select bank..."
                                                    isSearchable
                                                    styles={reactSelectStyles}
                                                />
                                                {errors.ddBankId && <p className="text-danger">{errors.ddBankId}</p>}
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4">
                                                <label className="form-labell">
                                                    Issuing Branch Name<span className="astrisk">*</span>
                                                </label>
                                            </div>
                                            <div className="col-md-8">
                                                <input
                                                    type="text"
                                                    className="form-control fm-modal"
                                                    value={issuingBranchId}
                                                    onChange={(e) => handleInputChange('issuingBranchId', e.target.value)}
                                                    placeholder="Enter issuing branch..."
                                                />
                                                {errors.issuingBranchId && (
                                                    <p className="text-danger">{errors.issuingBranchId}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4">
                                                <label className="form-labell">
                                                    Date of Issuing<span className="astrisk">*</span>
                                                </label>
                                            </div>
                                            <div className="col-md-8">
                                                <input
                                                    type="date"
                                                    className="form-control fm-modal"
                                                    value={ddIssuingDate}
                                                    onChange={(e) => handleInputChange('ddIssuingDate', e.target.value)}
                                                    max={new Date().toISOString().split('T')[0]}
                                                />
                                                {errors.ddIssuingDate && (
                                                    <p className="text-danger">{errors.ddIssuingDate}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4">
                                                <label className="form-labell">
                                                    Payee Name<span className="astrisk">*</span>
                                                </label>
                                            </div>
                                            <div className="col-md-8">
                                                <input
                                                    type="text"
                                                    className="form-control fm-modal"
                                                    value={ddpayeeName}
                                                    onChange={(e) => handleInputChange('ddpayeeName', e.target.value)}
                                                    placeholder="Enter payee name"
                                                />
                                                {errors.ddpayeeName && (
                                                    <p className="text-danger">{errors.ddpayeeName}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4">
                                                <label className="form-labell">
                                                    Payable At<span className="astrisk">*</span>
                                                </label>
                                            </div>
                                            <div className="col-md-8">
                                                <input
                                                    type="text"
                                                    className="form-control fm-modal"
                                                    value={payAbleAt}
                                                    onChange={(e) => handleInputChange('payAbleAt', e.target.value)}
                                                    placeholder="Enter city/location"
                                                />
                                                {errors.payAbleAt && <p className="text-danger">{errors.payAbleAt}</p>}
                                            </div>
                                        </div>

                                        <div className="row mt-3">
                                            <div className="col-md-4">
                                                <label className="form-labell">
                                                    Upload DD Image<span className="astrisk">*</span>
                                                </label>
                                            </div>
                                            <div className="col-md-8">
                                                <input
                                                    type="file"
                                                    accept="image/*,.pdf"
                                                    className="form-control fm-modal"
                                                    onChange={(e) => handleInputChange('ddImage', e.target.files[0])}
                                                />
                                                {errors.ddImage && <p className="text-danger">{errors.ddImage}</p>}
                                            </div>
                                        </div>
                                    </>
                                )}

                                {['NEFT', 'RTGS', 'IMPS'].includes(selectedRefundMode?.value) && (
                                    <>
                                        <div className="row">
                                            <div className="col-md-4">
                                                <label className="form-labell">
                                                    UTR Number<span className="astrisk">*</span>
                                                </label>
                                            </div>
                                            <div className="col-md-8">
                                                <input
                                                    type="text"
                                                    className="form-control fm-modal"
                                                    value={utrNumber}
                                                    onChange={(e) => handleInputChange('utrNumber', e.target.value)}
                                                    placeholder="Enter UTR number"
                                                />
                                                {errors.utrNumber && <p className="text-danger">{errors.utrNumber}</p>}
                                            </div>
                                        </div>

                                        <div className="row">
                                            <div className="col-md-4">
                                                <label className="form-labell">
                                                    Paid From Account<span className="astrisk">*</span>
                                                </label>
                                            </div>
                                            <div className="col-md-8">
                                                <Select
                                                    options={bankAccountOptions}
                                                    value={bankAccount}
                                                    onChange={(selected) => handleInputChange('bankAccount', selected)}
                                                    placeholder="Choose Bank Account..."
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                    styles={{
                                                        ...reactSelectStyles,
                                                        menuPortal: (base) => ({ ...base, zIndex: 99999999 }),
                                                    }}
                                                    menuPortalTarget={document.body}
                                                />
                                                {errors.bankAccount && (
                                                    <p className="text-danger">{errors.bankAccount}</p>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}

                                <div className="row">
                                    <div className="col-md-4">
                                        <label className="form-labell">
                                            Amount<span className="astrisk">*</span>
                                        </label>
                                    </div>
                                    <div className="col-md-8">
                                        <input
                                            type="text"
                                            min="0"
                                            className="form-control fm-modal"
                                            value={amount}
                                            ref={amountRef}
                                            onChange={(e) => setAmount(e.target.value.replace(/[^0-9]/g, ''))}
                                            onKeyDown={handleKeyPress}
                                            placeholder="Enter amount"
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-4">
                                        <label className="form-labell">Amount in words</label>
                                    </div>
                                    <div className="col-md-8 mt-3">
                                        <p>
                                            <strong style={{ color: '#7F56DA', fontWeight: 'bold' }}>
                                                {numberToWords(parseFloat(amount) || 0).toUpperCase()} ONLY
                                            </strong>
                                        </p>
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-md-4">
                                        <label className="form-labell">
                                            Reason for Refund<span className="astrisk">*</span>
                                        </label>
                                    </div>
                                    <div className="col-md-8">
                                        <input
                                            type="text"
                                            className="form-control fm-modal"
                                            value={remarks}
                                            // Capped to match MAX_PLACEHOLDER_CHARS in
                                            // finance/refunds/service.py — longer text is
                                            // truncated before it reaches WhatsApp.
                                            maxLength={200}
                                            onChange={(e) => handleInputChange('remarks', e.target.value)}
                                            placeholder="Why is this refund being issued?"
                                        />
                                        {errors.remarks && <p className="text-danger">{errors.remarks}</p>}
                                    </div>
                                </div>

                                <div className="mt-3">
                                    <div className="mb-3">
                                        <div
                                            className="d-flex justify-content-between align-items-center p-2 bg-light border rounded"
                                            onClick={() => setShowLedger(!showLedger)}
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <h6 className="mb-0">Ledger Summary</h6>
                                            <span>{showLedger ? '▲' : '▼'}</span>
                                        </div>

                                        {showLedger && (
                                            <div
                                                className="mt-2 paymentreporttablecontainer ledgerWrapper"
                                                style={{
                                                    maxHeight: '300px',
                                                    overflowY: 'auto',
                                                    border: '1px solid #ddd',
                                                    borderRadius: '6px',
                                                }}
                                            >
                                                <table className="table table-sm table-bordered table-hover student-table fee ledgerTable">
                                                    <thead className="ledgerHeader">
                                                        <tr>
                                                            <th style={{ padding: '10px', borderBottom: '2px solid #d0d0d0' }}>Date</th>
                                                            <th style={{ padding: '10px', borderBottom: '2px solid #d0d0d0' }}>Particular</th>
                                                            <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #d0d0d0' }}>Debit</th>
                                                            <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #d0d0d0' }}>Credit</th>
                                                            <th style={{ padding: '10px', textAlign: 'right', borderBottom: '2px solid #d0d0d0' }}>Balance</th>
                                                        </tr>
                                                    </thead>

                                                    <tbody className="ledgerBody">
                                                        {unifiedEntries.length > 0 ? (
                                                            (() => {
                                                                let runningBalance = 0;
                                                                return unifiedEntries.map((item, idx) => {
                                                                    if (item.entryType === 'Credit') runningBalance += item.amount;
                                                                    else if (item.entryType === 'Debit') runningBalance -= item.amount;

                                                                    return (
                                                                        <tr key={idx}>
                                                                            <td style={{ padding: '10px' }}>
                                                                                {item.entryType === 'Credit' && item.payment_date
                                                                                    ? formatDateDDMMYYYYHHMMSS_UTC(item.payment_date)
                                                                                    : formatDateDDMMYYYYHHMMSS_UTC(item.created_at)}
                                                                            </td>
                                                                            <td style={{ padding: '10px' }}>
                                                                                {item.entryType === 'Debit'
                                                                                    ? item.fee_head?.name || '-'
                                                                                    : (
                                                                                        <>
                                                                                            {item.mode_of_payment}
                                                                                            {item.transaction?.cheque_number && (
                                                                                                <div><strong>Cheque:</strong> {item.transaction.cheque_number}</div>
                                                                                            )}
                                                                                            {item.transaction?.dd_number && (
                                                                                                <div><strong>DD:</strong> {item.transaction.dd_number}</div>
                                                                                            )}
                                                                                            {item.transaction?.utr_number && (
                                                                                                <div><strong>UTR:</strong> {item.transaction.utr_number}</div>
                                                                                            )}
                                                                                        </>
                                                                                    )}
                                                                            </td>
                                                                            <td style={{ padding: '10px', textAlign: 'right' }}>
                                                                                ₹ {item.entryType === 'Debit'
                                                                                    ? Number(item.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })
                                                                                    : '0.00'}
                                                                            </td>
                                                                            <td style={{ padding: '10px', textAlign: 'right' }}>
                                                                                ₹ {item.entryType === 'Credit'
                                                                                    ? Number(item.amount).toLocaleString('en-IN', { minimumFractionDigits: 2 })
                                                                                    : '0.00'}
                                                                            </td>
                                                                            <td style={{ padding: '10px', textAlign: 'right' }}>
                                                                                ₹ {Number(runningBalance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                                            </td>
                                                                        </tr>
                                                                    );
                                                                });
                                                            })()
                                                        ) : (
                                                            <tr>
                                                                <td colSpan="5" className="text-center" style={{ padding: '10px' }}>
                                                                    No Ledger Entries Found
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </tbody>

                                                    <tfoot className="ledgerFooter">
                                                        <tr>
                                                            <td style={{ padding: '10px' }}></td>
                                                            <td style={{ padding: '10px' }}></td>
                                                            <td style={{ padding: '10px', textAlign: 'right' }}>
                                                                ₹ {Number(totalDebit).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                            </td>
                                                            <td style={{ padding: '10px', textAlign: 'right' }}>
                                                                ₹ {Number(totalCredit).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                            </td>
                                                            <td style={{ padding: '10px', textAlign: 'right' }}>
                                                                ₹ {Number(finalBalance).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                                                            </td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div
                    className="form-actions mt-5"
                    style={{
                        position: 'sticky',
                        background: 'white',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                    }}
                >
                    <button
                        type="submit"
                        disabled={isDisabled}
                        style={buttonStyle}
                        onClick={() => handleRefund('new')}
                    >
                        {loadingButton === 'new' ? (
                            <span className="spinner-border spinner-border-sm" role="status" />
                        ) : (
                            'Refund & New'
                        )}
                    </button>

                    <button
                        type="submit"
                        disabled={isDisabled}
                        style={buttonStyle}
                        onClick={() => handleRefund('close')}
                    >
                        {loadingButton === 'close' ? (
                            <span className="spinner-border spinner-border-sm" role="status" />
                        ) : (
                            'Refund & Close'
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
};
